import { Injectable, ErrorHandler } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { UserInfo } from '../../shared/models/user-info.model';
import { BusinessService } from '../models/business-service.model';
import { AuthEventType, IAuthEvent } from '../../auth/models/auth-event.model';
import { AuthService } from '../../auth/auth/auth.service';
import { UserService } from '../../shared/user/user.service';
import { environment } from '../../../environments/environment';


@Injectable()
export class BusinessFeesService {

  userInfo: UserInfo;

  constructor(private _http: HttpClient,
    private _auth: AuthService,
    private _userService: UserService,
    private _errorHandler: ErrorHandler, ) {
    this._auth.authStream.subscribe(e => this._onAuthEvent(e));
  }

  private async _onAuthEvent(e: IAuthEvent): Promise<void> {
    if (!e) {
      return;
    }
    switch (e.type) {
      case AuthEventType.SignIn:
        this.userInfo = await this._userService.getUserInfo();
        break;
      case AuthEventType.InvalidSignIn:
      case AuthEventType.SignOut:
        this.userInfo = null;
        break;
    }
  }
  /**
  * Method creates a new BusinessService in the API Server via a post request with the business id and a business service object.
  * @param businessId {number} used to identify the business
  * @param fee {BusinessService} business service object
  * @returns {Promise<BusinessService>} API Server will respond with a new BusinessService OBJ.
  */
  async createBusinessService(businessId: number, fee: BusinessService): Promise<BusinessService> {
    if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser) {
      const ERROR = new Error('User not authorized');
      this._errorHandler.handleError(ERROR);
      throw ERROR;
    }
    const URL = `${environment.airportsApiUrl}/business/${businessId}/service/`;
    try {
      const BODY = this._getFeeJSON(fee);
      const RESPONSE = await this._http.post<BusinessService>(URL, BODY).toPromise();
      const FEE = BusinessService.parse(RESPONSE);
      if (!FEE) {
        const ERROR = new Error('API did not return valid business service object');
        this._errorHandler.handleError(ERROR);
        throw ERROR;
      } else {
        return FEE;
      }
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
  * Sends put request to api server to update a given businessService.
  * Method updates the API Server with data from the json object with the service being passed in.
  * @param businessId {number} businessId
  * @param serviceId {number} the id of the BusinessService that will be updated
  * @param fee {BusinessService} content to be updated.
  * @returns {Promise<BusinessService>} API Server will respond with updated BusinessService OBJ.
  */
  async updateBusinessService(businessId: number, serviceId: number, fee: BusinessService): Promise<BusinessService> {
    if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser) {
      const ERROR = new Error('User not authorized');
      this._errorHandler.handleError(ERROR);
      throw ERROR;
    }
    const URL = `${environment.airportsApiUrl}/business/${businessId}/service/${serviceId}`;
    try {
      const BODY = this._getFeeJSON(fee);
      const RESPONSE = await this._http.put<BusinessService>(URL, BODY).toPromise();
      const FEE = BusinessService.parse(RESPONSE);
      if (!FEE) {
        const ERROR = new Error('API did not return valid business service object');
        this._errorHandler.handleError(ERROR);
        throw ERROR;
      } else {
        return FEE;
      }
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
  * Method to call API to delete a business service from a business
  * @param businessId {number} the business to remove the service from.
  * @param serviceId {number} the specific business service to be deleted.
  * @returns {Promise<boolean>} Returns boolean
  */
  async deleteBusinessService(businessId: number, serviceId: number): Promise<boolean> {
    if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser) {
      const ERROR = new Error('User not authorized');
      this._errorHandler.handleError(ERROR);
      throw ERROR;
    }
    const URL = `${environment.airportsApiUrl}/business/${businessId}/service/${serviceId}`;
    try {
      const RESPONSE = await this._http.delete(URL).toPromise();
      return true;
    } catch (e) {
      this._errorHandler.handleError(e);
      throw false;
    }
  }

  /**
   * converts business fee to json obj
   * @param {BusinessService} fee
   * @return {any}
   * @private
   */
  _getFeeJSON (fee: BusinessService): any {
    return {
      description: fee.description,
      fee: fee.fee,
      feeBasis: BusinessService.formatFeeBasis(fee.feeBasis),
      serviceNote: fee.serviceNote,
      aircraftType: fee.aircraftType,
      waiver: fee.waiver || false,
      waiverFuelQty: fee.waiverFuelQty
    };
  }

  /**
   * Gets all the fee types from the API
   * @returns {Promise<Array<string>>} FeeTypes
   */
  async getFeeTypes(): Promise<Array<string>> {
    const URL = `${environment.airportsApiUrl}/lookups?category=FeeTypes`;
    const feeTypes = new Array<string>();
    try {
      const RESPONSE = await this._http.get<any>(URL).toPromise();
      if (!RESPONSE) {
        const ERROR = new Error('API did not return valid business admin notes object');
        this._errorHandler.handleError(ERROR);
        throw ERROR;
      } else {
        for (let i = 0; i < RESPONSE.length; i++) {
          feeTypes.push(RESPONSE[i].lookup);
        }
        return feeTypes;
      }
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * Gets all the fee types from the API
   * @returns {Promise<Array<string>>} FeeTypes
   */
  async getAircraftTypes(): Promise<Array<string>> {
    const URL = `${environment.airportsApiUrl}/lookups?category=AircraftTypes`;
    const feeTypes = new Array<string>();
    try {
      const RESPONSE = await this._http.get<any>(URL).toPromise();
      if (!RESPONSE) {
        const ERROR = new Error('API did not return valid business admin notes object');
        this._errorHandler.handleError(ERROR);
        throw ERROR;
      } else {
        for (let i = 0; i < RESPONSE.length; i++) {
          feeTypes.push(RESPONSE[i].lookup);
        }
        return feeTypes;
      }
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }
}
